// client/src/components/chat/Chat.js
import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import './Chat.css'
import ChannelList from './ChannelList'
import MessageList from './MessageList'
import MessageComposer from './MessageComposer'
import useAuth from '../../hooks/useAuth'
import useChat from '../../hooks/useChat'
import useChatChannels from '../../hooks/useChatChannels'
import useChatMessages from '../../hooks/useChatMessages'
import useTypingIndicators from '../../hooks/useTypingIndicators'
import useOnlinePresence from '../../hooks/useOnlinePresence'
import ChatService from '../../services/ChatService'

/**
 * Chat Component
 *
 * Flexible chat interface that supports multiple modes:
 *
 * 1. Multi-channel mode (default):
 *    <Chat />
 *    Shows sidebar with all user's channels, allows switching between them
 *
 * 2. Single-channel mode:
 *    <Chat channelId="123" />
 *    Filters sidebar to show only the specified channel
 *
 * 3. Focused mode (no sidebar):
 *    <Chat channelId="123" hideSidebar={true} />
 *    Hides sidebar entirely, shows only the specified channel's messages
 *
 * 4. Division-scoped mode:
 *    <Chat divisionId="456" leagueId="789" />
 *    Shows only division chat, league chat, and DMs with players in that division
 *
 * 5. Division-scoped with initial selection:
 *    <Chat divisionId="456" leagueId="789" initialChannelId="123" />
 *    Same as #4 but auto-selects a specific channel on mount within the division-scoped list.
 *    Note: initialChannelId does NOT filter the sidebar; use channelId if you need filtering.
 *
 * Important: If both channelId and divisionId are provided, channelId takes precedence
 * and divisionId will be ignored. A development warning will be logged in this case.
 *
 * @param {string|number} channelId - Optional channel ID to filter/focus on (filters sidebar to show only this channel)
 * @param {boolean} hideSidebar - Whether to hide the channel sidebar completely
 * @param {string|number} divisionId - Optional division ID to filter channels by division
 * @param {string|number} leagueId - Optional league ID to include league chat
 * @param {string|number} initialChannelId - Optional channel ID to auto-select within the available channels (no additional filtering)
 */
function Chat({
  channelId = null,
  hideSidebar = false,
  divisionId = null,
  leagueId = null,
  initialChannelId = null,
}) {
  const { isLoggedIn } = useAuth()
  const [activeChannel, setActiveChannel] = useState(null)
  const [error, setError] = useState(null)
  const [chatConnected, setChatConnected] = useState(false)
  const [channelChangeAnnouncement, setChannelChangeAnnouncement] = useState('')

  // Track which channel IDs have had errors shown to prevent infinite loops
  const shownErrorsRef = useRef(new Set())
  // Track if we've warned about prop conflicts
  const hasWarnedRef = useRef(false)

  // Load channels
  const { channels, loadingChannels, loadChannels } = useChatChannels()

  // Warn in development if both channelId and divisionId are provided
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      channelId &&
      divisionId &&
      !hasWarnedRef.current
    ) {
      console.warn(
        'Chat: Both channelId and divisionId provided. channelId takes precedence and divisionId will be ignored.',
      )
      hasWarnedRef.current = true
    }
  }, [channelId, divisionId])

  // Filter channels based on channelId or divisionId/leagueId props
  const filteredChannels = useMemo(() => {
    // If channelId is provided, filter to just that channel
    if (channelId) {
      const channelIdStr = String(channelId)
      return channels?.filter((c) => c?.id?.toString() === channelIdStr) ?? []
    }

    // If divisionId is provided, filter to division-scoped channels
    if (divisionId) {
      const divisionIdNum = Number(divisionId)
      const leagueIdNum = leagueId ? Number(leagueId) : null

      return (
        channels?.filter((c) => {
          // Include division chat for this division
          if (
            c.type === 'division' &&
            c.division_id != null &&
            c.division_id === divisionIdNum
          )
            return true

          // Include league chat for this league (if leagueId provided)
          if (
            leagueIdNum &&
            c.type === 'league' &&
            c.league_id != null &&
            c.league_id === leagueIdNum
          )
            return true

          // Include DMs that belong to this division
          if (
            c.type === 'dm' &&
            c.division_id != null &&
            c.division_id === divisionIdNum
          )
            return true

          return false
        }) ?? []
      )
    }

    // No filtering - return all channels
    return channels
  }, [channels, channelId, divisionId, leagueId])

  // Manage messages
  const {
    messages,
    loadingMessages,
    loadMessages,
    loadMoreMessages,
    addMessage,
    clearMessages,
    sendMessage,
    editMessage,
    deleteMessage,
  } = useChatMessages(activeChannel, chatConnected)

  // Handle typing indicators
  const { getTypingUsersForChannel, startTyping, stopTyping } =
    useTypingIndicators(activeChannel, chatConnected)

  // Handle online presence
  const { getOnlineUsersForChannel, getOnlineCountForChannel } =
    useOnlinePresence(activeChannel, chatConnected)

  // Handle chat errors
  const handleChatError = useCallback((errorMessage) => {
    setError(errorMessage)
  }, [])

  // Handle new messages from WebSocket
  const handleNewMessage = useCallback(
    (message, action) => {
      addMessage(message, action)
    },
    [addMessage],
  )

  // Connect to chat service and get connection status
  const { chatConnected: hookChatConnected } = useChat(
    handleNewMessage,
    handleChatError,
  )

  // Sync the chat connected state
  useEffect(() => {
    setChatConnected(hookChatConnected)
  }, [hookChatConnected])

  // Keep track of which channels we've attempted to join for this connection
  const joinedForSessionRef = useRef(new Set())

  // When the socket connects (or reconnects), join all known channels
  useEffect(() => {
    // If we lost connection, reset the session joins so we re-join on next connect
    if (!chatConnected) {
      joinedForSessionRef.current = new Set()
      return
    }

    if (!Array.isArray(channels) || channels.length === 0) return

    // Join each channel once per connection session
    channels.forEach((c) => {
      const id = c?.id
      if (!id) return
      if (joinedForSessionRef.current.has(id)) return
      try {
        if (ChatService.isConnected) {
          ChatService.joinChannel(id)
          joinedForSessionRef.current.add(id)
        }
      } catch (e) {
        // Swallow and allow future attempts on next effect run
        console.warn('Failed to join channel', id, e)
      }
    })
  }, [chatConnected, channels])

  // Handle channel selection
  const handleChannelSelect = useCallback(
    (channel) => {
      setActiveChannel(channel)
      clearMessages() // Clear messages while loading new ones

      // Announce channel change to screen readers
      const channelName =
        channel?.name ||
        channel?.division_name ||
        channel?.league_name ||
        'Unknown channel'

      loadMessages(channel)
        .then(() => {
          setChannelChangeAnnouncement(`Switched to ${channelName}`)
        })
        .catch((error) => {
          setError(error.message)
          setChannelChangeAnnouncement(
            `Failed to switch to ${channelName}: ${error.message}`,
          )
        })
        .finally(() => {
          // Clear announcement after a brief delay
          setTimeout(() => setChannelChangeAnnouncement(''), 1000)
        })
    },
    [clearMessages, loadMessages],
  )

  // Auto-select channel based on props or first available channel
  useEffect(() => {
    // Early return if no channels loaded yet
    if (!filteredChannels || filteredChannels.length === 0) return

    if (channelId) {
      // Single-channel mode: find and select the specific channel
      const channelIdStr = String(channelId)
      const targetChannel = filteredChannels.find(
        (c) => c?.id?.toString() === channelIdStr,
      )
      if (targetChannel && targetChannel.id !== activeChannel?.id) {
        handleChannelSelect(targetChannel)
        // Clear any previous error when channel is found
        setError(null)
        shownErrorsRef.current.delete(channelIdStr)
      } else if (!targetChannel && !shownErrorsRef.current.has(channelIdStr)) {
        // Channel not found - handle once per channelId
        shownErrorsRef.current.add(channelIdStr)

        if (hideSidebar) {
          // Focused mode (no sidebar): show a clear, actionable error
          setError(
            'This conversation is not available. It may have been removed or you may not have access to it.',
          )
          setActiveChannel(null)
        } else {
          // Multi-channel mode: log a warning but keep the chat usable
          // so the user can select another channel from the sidebar.
          console.warn(
            `Channel ${channelId} not found or not accessible. Allowing user to continue with other channels.`,
          )
        }
      }
    } else if (initialChannelId) {
      // Division mode with initial channel selection
      const channelIdStr = String(initialChannelId)
      const targetChannel = filteredChannels.find(
        (c) => c?.id?.toString() === channelIdStr,
      )
      if (targetChannel && targetChannel.id !== activeChannel?.id) {
        handleChannelSelect(targetChannel)
        setError(null)
      }
    } else if (!activeChannel) {
      // Multi-channel mode: auto-select first channel
      handleChannelSelect(filteredChannels[0])
    }
  }, [
    activeChannel,
    filteredChannels,
    channelId,
    initialChannelId,
    handleChannelSelect,
    hideSidebar,
  ])

  // Handle sending messages
  const handleSendMessage = useCallback(
    async (body) => {
      try {
        await sendMessage(body)
      } catch (error) {
        console.error('Failed to send message:', error)
        throw error
      }
    },
    [sendMessage],
  )

  if (!isLoggedIn) {
    return (
      <div className="chat-container">
        <div className="empty-state">
          <div className="empty-state-icon">🔒</div>
          <h4 className="empty-state-title">Authentication Required</h4>
          <p className="empty-state-text">
            Please log in to access the chat system
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="chat-container">
        <div className="empty-state">
          <div className="empty-state-icon">⚠️</div>
          <h4 className="empty-state-title">Error</h4>
          <p className="empty-state-text">{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => {
              setError(null)
              loadChannels()
            }}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="chat-container" data-testid="chat-container">
      {/* Screen reader announcement for channel changes */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {channelChangeAnnouncement}
      </div>

      {!hideSidebar && (
        <ChannelList
          channels={filteredChannels}
          activeChannel={activeChannel}
          onChannelSelect={handleChannelSelect}
          loading={loadingChannels}
          getOnlineCountForChannel={getOnlineCountForChannel}
        />
      )}

      <div className="chat-main">
        <MessageList
          messages={messages}
          channel={activeChannel}
          loading={loadingMessages}
          onEditMessage={editMessage}
          onDeleteMessage={deleteMessage}
          onLoadMore={loadMoreMessages}
          getTypingUsersForChannel={getTypingUsersForChannel}
          getOnlineUsersForChannel={getOnlineUsersForChannel}
        />

        <MessageComposer
          onSendMessage={handleSendMessage}
          disabled={!activeChannel || !chatConnected}
          onStartTyping={startTyping}
          onStopTyping={stopTyping}
          autoFocus={Boolean(activeChannel)}
        />
      </div>
    </div>
  )
}

Chat.propTypes = {
  channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideSidebar: PropTypes.bool,
  divisionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leagueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialChannelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default Chat
